import { Alert, Box, Button, Paper, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { Link } from 'react-router-dom';
import type { EnrollmentDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { formatCents } from '../../lib/format';
import { Can } from '../auth/guards';
import { customerName, paymentLabel } from './labels';
import { WizardGate } from './wizard';

const TITLE = 'Enrollment complete';

function Done({ enrollment }: { enrollment: EnrollmentDto }) {
  const { customer, payment } = enrollment;
  const shipment = enrollment.handover?.type === 'shipment';
  const rows: Array<[string, string]> = [
    ['Enrollment ID', enrollment.enrollmentId],
    ['Customer ID', customer ? String(customer.customerId) : ''],
    ['Customer Name', customerName(enrollment.applicant)],
    shipment
      ? ['Model To Ship', enrollment.handover?.model?.name ?? '']
      : ['Telephone Number', customer?.mdn ?? enrollment.handover?.mdn ?? ''],
    ['Plan', enrollment.plan?.name ?? ''],
    [
      'Amount Paid',
      payment ? `${formatCents(payment.amountCents)} (${paymentLabel(payment.method)})` : '',
    ],
  ];

  return (
    <Box>
      <PageHeader title={TITLE} code="PC1142" />
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 640 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {shipment
            ? `Enrollment ${enrollment.enrollmentId} is complete. The order is in the shipping queue; the line is activated after the SIM has been sent.`
            : `Enrollment ${enrollment.enrollmentId} is complete and the line is active.`}
        </Alert>
        <Table aria-label="Enrollment confirmation" sx={{ mb: 2 }}>
          <TableBody>
            {rows.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600, width: 200 }}>
                  {label}
                </TableCell>
                <TableCell>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {customer ? (
            <Can code="PC56">
              <Button variant="contained" component={Link} to={`/customers/${customer.id}`}>
                Open Customer Profile
              </Button>
            </Can>
          ) : null}
          <Can code="PC1142">
            <Button variant="outlined" component={Link} to="/enrollments/new">
              New enrollment
            </Button>
          </Can>
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * Confirmation shown after payment and activation. Not seen in the reference (its wizard was
 * walked to step 3 only); it shows what the user needs to hand the customer their number.
 */
export function DonePage() {
  return (
    <WizardGate step="done" title={TITLE}>
      {(enrollment) => <Done enrollment={enrollment} />}
    </WizardGate>
  );
}
